import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  active_account_id: string | null;
  locale: string;
  default_symbol: string | null;
  default_risk_mode: 'percent_equity' | 'fixed_amount';
  timezone: string;
  notification_in_app: boolean;
  notification_email: boolean;
  ui_compact_mode: boolean;
  sl_alert_60: boolean;
  sl_alert_80: boolean;
  sl_alert_90: boolean;
  // Appearance
  ui_density?: 'compact' | 'comfortable' | 'spacious' | null;
  font_size?: 'sm' | 'md' | 'lg' | 'xl' | null;
  border_radius_style?: 'sharp' | 'rounded' | 'pill' | null;
  animations_mode?: 'full' | 'reduced' | 'off' | null;
  accent_color?: 'blue' | 'green' | 'purple' | 'orange' | null;
  // Localization
  calendar_type?: 'gregorian' | 'jalali' | null;
  date_format?: 'iso' | 'eu' | 'us' | 'fa' | null;
  time_format?: '24h' | '12h' | null;
  number_format?: 'us' | 'eu' | 'fa' | null;
  first_day_of_week?: 'sunday' | 'monday' | 'saturday' | null;
  display_currency?: string | null;
  // Data Display
  default_trades_view?: 'table' | 'cards' | 'heatmap' | null;
  default_group_by?: 'none' | 'symbol' | 'strategy' | 'day' | null;
  default_date_range?: 'today' | 'week' | 'month' | 'ytd' | 'all' | null;
  pnl_display_mode?: 'currency' | 'pips' | 'r_multiple' | null;
  default_chart_type?: 'line' | 'bar' | 'candle' | null;
  show_mock_data?: boolean | null;
  // Workspace
  default_landing_page?: 'dashboard' | 'trades' | 'analytics' | 'last' | null;
  sidebar_default?: 'expanded' | 'collapsed' | 'auto' | null;
  restore_session?: boolean | null;
  keyboard_shortcuts_enabled?: boolean | null;
  show_breadcrumbs?: boolean | null;
  // Trading Defaults
  default_risk_percent?: number | null;
  default_rr?: number | null;
  default_sl_method?: 'pip' | 'atr' | 'structure' | null;
  default_timeframe?: 'M5' | 'M15' | 'H1' | 'H4' | 'D1' | null;
  created_at: string;
  updated_at: string;
}

export const useProfile = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as Profile | null;
    },
    enabled: !!user,
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ userId, updates }: { userId: string; updates: Partial<Profile> }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['profile', variables.userId] });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useSetActiveAccount = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (accountId: string | null) => {
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ active_account_id: accountId })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['accounts', 'active'] });
      toast({
        title: 'Active account updated',
        description: 'Your active trading account has been changed.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
